/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { SearchExpand, SideNavSection } from '@repo/shared-client/components/blocks'
import {
  InputFormField,
  NativeSelectFormField,
  SliderFormField,
  SwitchFormField,
  TagMultiSelectFormField,
  tagMultiSelectFromTags,
} from '@repo/shared-client/components/fields'
import { Button, Card, CardContent, ToggleGroup, ToggleGroupItem } from '@repo/shared-client/components/ui'
import { Form, useFormContext } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { SFMapBoxSearchBoxInputTheme } from '@repo/shared-client/mapbox'
import SFJoinButton from '@web/src/components/primatives/sf-join-button/sf-join-button'
import { useEventCategoryOptions } from '@web/src/features/event/use-event-category-options'
import { useTranslation } from '@web/src/lib/intl/react'
import { FolderOpenIcon, FunnelPlusIcon, ListOrderedIcon, MapIcon, MapPinIcon, TagIcon } from 'lucide-react'
import { type ReactNode, useCallback, useState } from 'react'
import SFInfoPopup from 'src/components/primatives/sf-info-popup/sf-info-popup'
import SFLocationSearchField from 'src/components/primatives/sf-location-search-field/sf-location-search-field'
import type { EventListFilterFormValues } from '../event-list-filter-types'
import type { EventListSideNavProps } from '../event-list-side-nav/event-list-side-nav'
import { useEventListFilterFields, useEventListFilterForm } from '../use-event-list-filter-form'

export type EventListTopNavProps = EventListSideNavProps & {
  rightBarChildren?: ReactNode
}

function EventListTopNavFields({
  tags,
  isAuthenticated,
  hasPermission,
  defaultLocation,
  filterResetValues,
  onFiltersApply,
  onReset,
  rightBarChildren,
  showMyEvents,
}: Omit<EventListTopNavProps, 'filterDefaults'>) {
  const { t } = useTranslation()
  const { selectOptions: categoryOptions } = useEventCategoryOptions()
  const { reset, setValue } = useFormContext<EventListFilterFormValues>()
  const { triggerFilterChange, distance, handleSearchChange } = useEventListFilterFields(onFiltersApply)
  const [tagMode, setTagMode] = useState<'include' | 'exclude'>('include')

  const handleTagModeChange = useCallback(
    (nextValue: string) => {
      const newMode = nextValue as 'include' | 'exclude'
      if (newMode === tagMode) return
      if (newMode === 'include') {
        setValue('excludeTags', [])
      } else {
        setValue('tags', [])
      }
      setTagMode(newMode)
      triggerFilterChange(newMode === 'include' ? { excludeTags: [] } : { tags: [] })
    },
    [tagMode, setValue, triggerFilterChange]
  )

  const handleResetFilters = useCallback(() => {
    reset(filterResetValues)
    setTagMode('include')
    onReset()
  }, [filterResetValues, onReset, reset])

  const filtersDisabled = !isAuthenticated

  return (
    <div className="flex w-full flex-col items-stretch gap-2">
      <SearchExpand
        clearSearchLabel={t('Clear search')}
        filterControlsDisabled={filtersDisabled}
        inputProps={{ 'aria-label': t('Search events') }}
        minChars={0}
        name="name"
        onSearchChange={handleSearchChange}
        placeholder={t('Search by name...')}
        rightBarChildren={rightBarChildren}
        toggleFiltersLabel={t('Toggle filters')}
      >
        <Card>
          <CardContent className="flex flex-col gap-4">
            <SideNavSection className="my-0" icon={<ListOrderedIcon />} label={t('Sort By')}>
              <NativeSelectFormField
                disabled={filtersDisabled}
                name="sortBy"
                onValueChange={() => triggerFilterChange()}
              >
                <option value="START_DATE">{t('Start Date')}</option>
                <option value="DISTANCE">{t('Distance')}</option>
                <option value="LATEST">{t('Latest')}</option>
              </NativeSelectFormField>
            </SideNavSection>

            {isAuthenticated && (
              <>
                <SideNavSection className="my-0" icon={<MapPinIcon />} label={t('Distance Within')}>
                  <div className="flex w-full items-center justify-between gap-4 py-0 text-sm">
                    <SliderFormField
                      disabled={filtersDisabled}
                      name="distance"
                      onValueChangeEnd={() => triggerFilterChange()}
                      sliderProps={{
                        className: 'flex-1 min-w-0',
                        max: 1000,
                        min: 10,
                      }}
                    />
                    <span className="min-w-[50px] whitespace-nowrap text-right">
                      {distance?.[0] < 1000 ? t('{{value}} km', { value: distance[0] }) : t('ALL')}
                    </span>
                  </div>
                </SideNavSection>

                <SideNavSection className="my-0" icon={<MapIcon />} label={t('Distance From')}>
                  {hasPermission('location:change-location') ? (
                    <div className={cn('w-full', filtersDisabled && 'pointer-events-none opacity-60')}>
                      <SFLocationSearchField
                        name="location"
                        onLocationChange={() => triggerFilterChange()}
                        placeholder={t('Enter starting location')}
                        theme={SFMapBoxSearchBoxInputTheme}
                      />
                    </div>
                  ) : (
                    <div className="flex w-full items-center justify-center">
                      <InputFormField
                        disabled
                        inputProps={{
                          value: defaultLocation,
                          readOnly: true,
                        }}
                        name="locationPlaceholder"
                      />
                      <SFInfoPopup
                        description={t('Adjusting this is available to supporters only')}
                        iconButtonProps={{ className: 'mt-7' }}
                        title={t('Supporters Only')}
                      />
                    </div>
                  )}
                </SideNavSection>
              </>
            )}

            <SideNavSection className="my-0" icon={<FolderOpenIcon />} label={t('Category')}>
              <TagMultiSelectFormField
                disabled={filtersDisabled}
                name="categories"
                onValueChange={() => triggerFilterChange()}
                onValueChangeTrigger="close"
                options={categoryOptions}
                placeholder={t('All')}
              />
            </SideNavSection>

            <SideNavSection
              className="my-0"
              icon={<TagIcon />}
              label={t('Tags')}
              labelEnd={
                <ToggleGroup
                  className="shrink-0"
                  disabled={filtersDisabled}
                  multiple={false}
                  onValueChange={(groupValue) => {
                    const next = groupValue[0]
                    if (!next) return
                    handleTagModeChange(next)
                  }}
                  size="sm"
                  spacing={0}
                  value={[tagMode]}
                  variant="outline"
                >
                  <ToggleGroupItem value="include">{t('Include')}</ToggleGroupItem>
                  <ToggleGroupItem value="exclude">{t('Exclude')}</ToggleGroupItem>
                </ToggleGroup>
              }
            >
              <div className="flex w-full flex-col items-stretch">
                {tagMode === 'include' ? (
                  <TagMultiSelectFormField
                    disabled={filtersDisabled}
                    name="tags"
                    onValueChange={() => triggerFilterChange()}
                    onValueChangeTrigger="close"
                    options={tagMultiSelectFromTags(tags)}
                    placeholder={t('All')}
                  />
                ) : (
                  <TagMultiSelectFormField
                    disabled={filtersDisabled}
                    name="excludeTags"
                    onValueChange={() => triggerFilterChange()}
                    onValueChangeTrigger="close"
                    options={tagMultiSelectFromTags(tags)}
                    placeholder={t('None')}
                  />
                )}
              </div>
            </SideNavSection>

            <SideNavSection className="my-0" icon={<FunnelPlusIcon />} label={t('Other Filters')}>
              <div className="flex flex-col items-stretch gap-3 py-1">
                {isAuthenticated && (
                  <SwitchFormField
                    disabled={filtersDisabled}
                    label={t('Past Events')}
                    name="pastEvents"
                    onCheckedChange={(checked) => triggerFilterChange({ pastEvents: checked })}
                  />
                )}

                {showMyEvents && (
                  <SwitchFormField
                    disabled={filtersDisabled}
                    label={t('Hosting')}
                    name="showHosting"
                    onCheckedChange={(checked) => triggerFilterChange({ showHosting: checked })}
                  />
                )}
              </div>
            </SideNavSection>

            {isAuthenticated && (
              <div className="flex w-full items-center justify-end">
                <Button onClick={handleResetFilters} type="button" variant="secondary">
                  {t('Reset Filters')}
                </Button>
              </div>
            )}
            {!isAuthenticated && <SFJoinButton className="w-full" size="sm" />}
          </CardContent>
        </Card>
      </SearchExpand>
    </div>
  )
}

export default function EventListTopNav({
  tags,
  isAuthenticated,
  hasPermission,
  defaultLocation,
  filterDefaults,
  filterResetValues,
  onFiltersApply,
  onReset,
  rightBarChildren,
  showMyEvents,
}: EventListTopNavProps) {
  const formMethods = useEventListFilterForm(filterDefaults)

  return (
    <Form formMethods={formMethods} suppressHydrationWarning>
      <EventListTopNavFields
        defaultLocation={defaultLocation}
        filterResetValues={filterResetValues}
        hasPermission={hasPermission}
        isAuthenticated={isAuthenticated}
        onFiltersApply={onFiltersApply}
        onReset={onReset}
        rightBarChildren={rightBarChildren}
        showMyEvents={showMyEvents}
        tags={tags}
      />
    </Form>
  )
}
